import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { SPECIAL_EXISTING_SHIPPING_METHODS_CHECKOUT_EXPERIENCE } from "../checkouts/checkout-ab-test-utils";
import { CheckoutExperience } from "../integration/redo-in-shipping/ris";
import { RateTableType } from "../integration/redo-in-shipping/shipping-rate-table";
import { ShopifyPlan } from "../integration/shopify/shopify-plan";
import {
  ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_AVAILABLE_DATE,
  CheckoutAnalyticsVersion,
  getCheckoutAnalyticsVersionForSplit,
  isAdvancedCheckoutAnalyticsVersion,
} from "./checkout-conversion-metrics/analytics";

/**
 * Currently, whether advanced checkout features are available is determined by
 * whether the team has Shopify Plus. This check is thin right now, but it
 * exists to centralize the logic for checking if advanced checkout features
 * are available (to easily track all the features that are conditioned on it).
 */
export function isAdvancedCheckoutFeaturesAvailable({
  hasShopifyPlus,
  planDisplayName,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return (
    hasShopifyPlus ||
    planDisplayName === "Plus Trial" ||
    !!(teamId && SHOP_PLUS_OVERRIDE_TEAM_IDS.includes(teamId))
  );
}

const SHOP_PLUS_OVERRIDE_TEAM_IDS = [
  // Dev teams
  "67e58c8b02978c77ac941989", // [Dev] Redo | Coleman 2
  "67d334c95d63bb9f8f7aa3ac", // [Dev] Redo | Ben Carper 2
];

export function isCheckoutMetricsInsightsAvailable({
  hasShopifyPlus,
  planDisplayName,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return isAdvancedCheckoutFeaturesAvailable({
    hasShopifyPlus,
    planDisplayName,
    teamId,
  });
}

export function shouldShowSegmentedCheckoutMetricsTable({
  hasShopifyPlus,
  planDisplayName,
  splitStartDate,
  splitAnalyticsVersionOverride,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  splitStartDate: Date | undefined;
  splitAnalyticsVersionOverride: CheckoutAnalyticsVersion | undefined;
  teamId: string;
}): boolean {
  const _isAdvancedCheckoutFeaturesAvailable =
    isAdvancedCheckoutFeaturesAvailable({
      hasShopifyPlus,
      planDisplayName,
      teamId,
    });
  const analyticsVersion = getCheckoutAnalyticsVersionForSplit({
    shouldUseAdvanced: _isAdvancedCheckoutFeaturesAvailable,
    splitStartDate,
    splitAnalyticsVersionOverride,
  });
  const _isAdvancedCheckoutAnalyticsVersion =
    isAdvancedCheckoutAnalyticsVersion(analyticsVersion);
  const hasStarted = isDef(splitStartDate);
  const startDateIsAfterMetricsAvailableDate =
    splitStartDate &&
    splitStartDate.getTime() >
      ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_AVAILABLE_DATE.getTime();
  return (
    _isAdvancedCheckoutAnalyticsVersion &&
    hasStarted &&
    !!startDateIsAfterMetricsAvailableDate
  );
}

export function shouldShowCustomVisualsForSplit({
  hasShopifyPlus,
  planDisplayName,
  teamId,
  splitStartDate,
  splitAnalyticsVersionOverride,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
  splitStartDate: Date | undefined;
  splitAnalyticsVersionOverride: CheckoutAnalyticsVersion | undefined;
}): boolean {
  const _isAdvancedCheckoutFeaturesAvailable =
    isAdvancedCheckoutFeaturesAvailable({
      hasShopifyPlus,
      planDisplayName,
      teamId,
    });
  const analyticsVersion = getCheckoutAnalyticsVersionForSplit({
    shouldUseAdvanced: _isAdvancedCheckoutFeaturesAvailable,
    splitStartDate,
    splitAnalyticsVersionOverride,
  });
  switch (analyticsVersion) {
    case "advanced-v09-16-2025":
      // Custom visuals rely on the existence of the advanced checkout delivery
      // options metrics existing. This is the first version with those metrics
      // available.
      return true;
    case "advanced-v08-13-2025":
    case "basic":
      return false;
    default:
      return assertNever(analyticsVersion);
  }
}

export function isCheckoutWidgetsAvailable({
  shopifyPlan,
  planDisplayName,
  teamId,
}: {
  shopifyPlan: ShopifyPlan;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return isAdvancedCheckoutFeaturesAvailable({
    hasShopifyPlus: shopifyPlan === ShopifyPlan.PLUS,
    planDisplayName,
    teamId,
  });
}

/**
 * Checks if predictive conversion is fully configured with both the feature
 * enabled AND a SageMaker endpoint configured.
 */
export function isPredictiveConversionFullyConfigured({
  predictiveConversion,
}: {
  predictiveConversion:
    | {
        enabled?: boolean;
        activeCheckoutHazardModel?: { endpointName?: string };
      }
    | undefined;
}): boolean {
  return (
    !!predictiveConversion?.enabled &&
    !!predictiveConversion.activeCheckoutHazardModel?.endpointName
  );
}

/**
 * Checks if predictive conversion is enabled, regardless of whether a
 * SageMaker endpoint is live and configured with a model.
 */
export function isPredictiveConversionEnabled({
  predictiveConversion,
}: {
  predictiveConversion: { enabled?: boolean } | undefined;
}): boolean {
  return !!predictiveConversion?.enabled;
}

// ─────────────────────────────────────────────────────────────────────────────
//   Shipping option metafield feature compatibility
//
//   When `useShippingOptionMetafields` is enabled, the delivery customization
//   function can no longer parse encoded service codes. Several features that
//   depend on service code data (rate_table_id, treatment_ids, redo_coverage,
//   preview_mode) stop working. These functions detect incompatible features
//   and guard against enabling conflicting configurations.
//
//   IMPORTANT: The `activeCheckoutExperiences` param should be the set of experiences
//   reachable from the active checkout tree, not the full
//   shippingCoverage.activeCheckoutExperiences array.
// ─────────────────────────────────────────────────────────────────────────────

interface CheckoutOptimizationSettingsForMetafieldCheck {
  useShippingOptionMetafields?: boolean | null;
}

export function isUsingShippingOptionMetafields({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !!checkoutOptimizationSettings?.useShippingOptionMetafields;
}

export function isUsingAutoSelectedShippingOptions({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<
    CheckoutExperience,
    "customShippingMethodSortOrder"
  >[];
}): boolean {
  return activeCheckoutExperiences.some(
    (ce) =>
      isDef(ce.customShippingMethodSortOrder) &&
      ce.customShippingMethodSortOrder.length > 0,
  );
}

export function isUsingChris({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<CheckoutExperience, "rateTables">[];
}): boolean {
  return activeCheckoutExperiences.some((ce) =>
    ce.rateTables.some((rt) => {
      const hasCoverage =
        rt.coverageTypes?.return === true ||
        rt.coverageTypes?.packageProtection === true;
      const hasMerchantPaidCoverage = (() => {
        if (rt.type !== RateTableType.FIXED) {
          return false;
        }
        return rt.rates.some(
          (rate) =>
            rate.merchantPaidCoverageTypes?.return === true ||
            rate.merchantPaidCoverageTypes?.packageProtection === true,
        );
      })();
      return hasCoverage || hasMerchantPaidCoverage;
    }),
  );
}

export function isUsingHalfRedoAbTest({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: Pick<CheckoutExperience, "_id">[];
}): boolean {
  return activeCheckoutExperiences.some(
    (ce) =>
      ce._id === SPECIAL_EXISTING_SHIPPING_METHODS_CHECKOUT_EXPERIENCE._id,
  );
}

/**
 * Returns true if shipping option metafields can be enabled. This requires
 * that no incompatible features are in use across the active checkout
 * experiences.
 */
export function canUseShippingOptionMetafields({
  activeCheckoutExperiences,
}: {
  activeCheckoutExperiences: CheckoutExperience[];
}): boolean {
  return (
    !isUsingAutoSelectedShippingOptions({ activeCheckoutExperiences }) &&
    !isUsingChris({ activeCheckoutExperiences }) &&
    !isUsingHalfRedoAbTest({ activeCheckoutExperiences })
  );
}

export function canUseAutoSelectedShippingOptions({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}

export function canUseChris({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}

export function canUseHalfRedoAbTest({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}

export function canUsePreviewMode({
  checkoutOptimizationSettings,
}: {
  checkoutOptimizationSettings:
    | CheckoutOptimizationSettingsForMetafieldCheck
    | null
    | undefined;
}): boolean {
  return !isUsingShippingOptionMetafields({ checkoutOptimizationSettings });
}
